import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Instagram, Twitter, Linkedin, ArrowRight, Globe, MapPin, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_gen-connect-11/artifacts/5jr50pac_gh%20logo.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.already_subscribed ? "You're already on the list." : "Thanks for joining us.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-navy text-white mt-24" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 pb-14 border-b border-white/10">
          <div>
            <p className="eyebrow !text-teal mb-3">Stay connected</p>
            <h3 className="font-display text-3xl sm:text-4xl leading-[1.1] max-w-md text-white">
              Updates, stories &amp; new features — right to your inbox.
            </h3>
          </div>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row items-stretch gap-3 self-end w-full" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="gh-input flex-1 !bg-white/5 !border-white/20 !text-white placeholder:!text-white/50"
              data-testid="footer-newsletter-email"
            />
            <button type="submit" disabled={loading} className="btn-primary whitespace-nowrap" data-testid="footer-newsletter-submit">
              {loading ? "Subscribing..." : "Subscribe"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 py-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center overflow-hidden">
                <img src={LOGO_URL} alt="Generational Health" className="w-full h-full object-contain scale-[1.5]" />
              </div>
              <div className="leading-tight">
                <p className="font-display text-xl" data-no-translate>generational <span className="text-teal">+Health</span></p>
                <p className="text-xs tracking-[0.18em] uppercase text-white/60 font-semibold mt-0.5">Your mind. Your power.</p>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">
              A HIPAA-compliant mental health platform built to break generational cycles of trauma — and create pathways to healing, stability, and long-term wellness.
            </p>
            <div className="flex items-center gap-2 mt-5 text-xs text-white/70">
              <Globe className="w-3.5 h-3.5 text-teal" /> generationalh.com
            </div>
            <div className="mt-4 space-y-2 text-xs text-white/70">
              <a href="tel:7049999056" className="flex items-center gap-2 hover:text-teal transition" data-testid="footer-phone-link">
                <Phone className="w-3.5 h-3.5 text-teal" /> 704-999-9056
              </a>
              <a href="https://maps.google.com/?q=719+East+Blvd+Charlotte+NC+28203" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-teal transition" data-testid="footer-address-link">
                <MapPin className="w-3.5 h-3.5 text-teal mt-0.5 flex-shrink-0" />
                <span>719 East Blvd<br />Charlotte, NC 28203</span>
              </a>
            </div>
          </div>

          <div>
            <p className="eyebrow !text-white/60 mb-4">Product</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/features" className="hover:text-teal transition">App Features</Link></li>
              <li><Link to="/for-providers" className="hover:text-teal transition">For Providers</Link></li>
              <li><Link to="/download" className="hover:text-teal transition">Download the App</Link></li>
              <li><Link to="/events" className="hover:text-teal transition">Events</Link></li>
              <li><Link to="/resources" className="hover:text-teal transition">Resources</Link></li>
              <li><Link to="/stories" className="hover:text-teal transition">Stories</Link></li>
              <li><Link to="/faq" className="hover:text-teal transition">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <p className="eyebrow !text-white/60 mb-4">Organization</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="hover:text-teal transition">About Us</Link></li>
              <li><Link to="/team" className="hover:text-teal transition">Team</Link></li>
              <li><Link to="/partner" className="hover:text-teal transition">Partner With Us</Link></li>
              <li><Link to="/donate" className="hover:text-teal transition">Donate</Link></li>
              <li><Link to="/contact" className="hover:text-teal transition">Contact</Link></li>
            </ul>
          </div>

          <div>
            <p className="eyebrow !text-white/60 mb-4">In Crisis?</p>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="tel:988" className="flex items-center gap-2 hover:text-teal transition">
                  <Phone className="w-4 h-4" /> Call or text 988
                </a>
              </li>
              <li>
                <a href="sms:741741" className="flex items-center gap-2 hover:text-teal transition">
                  <Mail className="w-4 h-4" /> Text HOME to 741741
                </a>
              </li>
              <li>
                <a href="tel:911" className="flex items-center gap-2 hover:text-teal transition">
                  <Phone className="w-4 h-4" /> 911 — Emergencies
                </a>
              </li>
            </ul>
            <div className="flex gap-3 mt-6">
              <a href="https://www.instagram.com/generationalhealth1/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-teal transition" data-testid="footer-instagram-link"><Instagram className="w-4 h-4" /></a>
              <a href="#" aria-label="Twitter" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-teal transition"><Twitter className="w-4 h-4" /></a>
              <a href="#" aria-label="LinkedIn" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-teal transition"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10 text-xs text-white/60">
          <span>© {new Date().getFullYear()} Generational Health. Your mind. Your power.</span>
          <div className="flex items-center gap-5 flex-wrap justify-center">
            <span className="inline-flex items-center gap-1.5 text-teal"><ShieldCheck className="w-3.5 h-3.5" /> HIPAA · Real-time integrity monitoring</span>
            <Link to="/terms" className="hover:text-teal transition" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-teal transition" data-testid="footer-privacy-link">Privacy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
